/*
 * ChecklistItem.jsx
 * -----------------
 * Single item row inside a checklist.
 * Features an animated custom checkbox, priority indicator,
 * due date display, skipped badge, expandable notes field,
 * and a drag handle for reordering.
 * Checking an item triggers a slide-out animation and fires
 * the onCheck callback so the parent can show an undo toast.
 * Used in ChecklistDetail and Dashboard summary sections.
 */

import React, { useState, useRef } from "react";
import CategoryTag from "./CategoryTag";

// ============================================
// PRIORITY CONFIG
// ============================================
const PRIORITY_CONFIG = {
  high:   { color: "var(--priority-high)",   label: "High",   dot: "🔴" },
  medium: { color: "var(--priority-medium)", label: "Medium", dot: "🟡" },
  low:    { color: "var(--priority-low)",    label: "Low",    dot: "⚪" },
};

// ============================================
// CUSTOM CHECKBOX
// ============================================
function Checkbox({ checked, onChange, priority }) {
  const color = PRIORITY_CONFIG[priority]?.color || "var(--accent-green)";

  return (
    <button
      onClick={onChange}
      style={{
        width: "22px",
        height: "22px",
        borderRadius: "6px",
        border: `2px solid ${checked ? color : "var(--border-strong)"}`,
        background: checked ? color : "transparent",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "border-color 200ms ease, background 200ms ease",
        padding: 0
      }}
    >
      {/* Checkmark SVG */}
      <svg
        width="12"
        height="10"
        viewBox="0 0 12 10"
        fill="none"
        style={{
          opacity: checked ? 1 : 0,
          transform: checked ? "scale(1)" : "scale(0.5)",
          transition: "opacity 200ms ease, transform 200ms ease"
        }}
      >
        <path
          d="M1 5L4.5 8.5L11 1"
          stroke="white"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

// ============================================
// DUE DATE DISPLAY
// ============================================
function DueDate({ dueDate }) {
  if (!dueDate) return null;

  const due = new Date(dueDate);
  const now = new Date();
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);

  const isOverdue = due < todayStart;
  const isToday = due >= todayStart && due <= todayEnd;

  const label = isToday
    ? "Today"
    : due.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  const color = isOverdue
    ? "var(--status-overdue)"
    : isToday
      ? "var(--status-today)"
      : "var(--text-muted)";

  return (
    <span style={{
      fontFamily: "var(--font-body)",
      fontSize: "11px",
      fontWeight: isOverdue || isToday ? 600 : 400,
      color,
      display: "flex",
      alignItems: "center",
      gap: "3px"
    }}>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <rect x="1" y="2" width="8" height="7" rx="1" stroke="currentColor" strokeWidth="1.2"/>
        <path d="M3 1V3M7 1V3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
        <path d="M1 5H9" stroke="currentColor" strokeWidth="1.2"/>
      </svg>
      {isOverdue ? `Overdue · ${label}` : label}
    </span>
  );
}

// ============================================
// SKIPPED BADGE
// ============================================
function SkippedBadge({ skippedOn }) {
  if (!skippedOn) return null;

  const date = new Date(skippedOn).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });

  return (
    <span style={{
      fontFamily: "var(--font-body)",
      fontSize: "11px",
      fontWeight: 500,
      color: "var(--text-muted)",
      background: "var(--bg-surface-2)",
      border: "1px solid var(--border)",
      borderRadius: "100px",
      padding: "1px 7px",
      display: "inline-flex",
      alignItems: "center",
      gap: "3px"
    }}>
      ↩ Skipped {date}
    </span>
  );
}

// ============================================
// DRAG HANDLE
// ============================================
function DragHandle({ listeners, attributes }) {
  return (
    <div
      {...listeners}
      {...attributes}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "3px",
        padding: "var(--space-2)",
        cursor: "grab",
        color: "var(--text-muted)",
        flexShrink: 0,
        touchAction: "none"
      }}
    >
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          display: "flex",
          gap: "3px"
        }}>
          <div style={{ width: "3px", height: "3px", borderRadius: "50%", background: "currentColor" }} />
          <div style={{ width: "3px", height: "3px", borderRadius: "50%", background: "currentColor" }} />
        </div>
      ))}
    </div>
  );
}

// ============================================
// NOTES FIELD
// ============================================
function NotesField({ notes, onChange, readOnly }) {
  const [expanded, setExpanded] = useState(!!notes);
  const textareaRef = useRef(null);

  function handleExpand() {
    setExpanded(true);
    setTimeout(() => textareaRef.current?.focus(), 50);
  }

  return (
    <div style={{ marginTop: "var(--space-2)" }}>
      {!expanded ? (
        <button
          onClick={handleExpand}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            color: "var(--text-muted)",
            padding: 0,
            display: "flex",
            alignItems: "center",
            gap: "4px",
            transition: "color var(--transition-fast)"
          }}
          onMouseEnter={e => e.currentTarget.style.color = "var(--accent-green)"}
          onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 3H11M1 6H8M1 9H6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
          </svg>
          {notes ? "View note" : "Add note"}
        </button>
      ) : (
        <textarea
          ref={textareaRef}
          value={notes || ""}
          onChange={e => onChange?.(e.target.value)}
          readOnly={readOnly}
          placeholder="Add a note..."
          rows={2}
          style={{
            width: "100%",
            fontFamily: "var(--font-body)",
            fontSize: "13px",
            color: "var(--text-secondary)",
            background: "var(--bg-surface-2)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            padding: "var(--space-2) var(--space-3)",
            resize: "vertical",
            outline: "none",
            lineHeight: 1.5,
            transition: "border-color var(--transition-fast)"
          }}
          onFocus={e => e.target.style.borderColor = "var(--accent-green)"}
          onBlur={e => e.target.style.borderColor = "var(--border)"}
        />
      )}
    </div>
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function ChecklistItem({
  item,
  onCheck,
  onUpdate,
  onDelete,
  dragHandleListeners,
  dragHandleAttributes,
  isDragging = false,
  readOnly = false
}) {
  const [leaving, setLeaving] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [editingText, setEditingText] = useState(false);
  const [text, setText] = useState(item.text);

  // ============================================
  // CHECK OFF ITEM -- animate out then fire callback
  // ============================================
  function handleCheck() {
    if (readOnly || item.checked) return;
    setLeaving(true);
    setTimeout(() => {
      onCheck(item);
    }, 400);
  }

  // ============================================
  // SAVE EDITED TEXT
  // ============================================
  function handleTextSave() {
    setEditingText(false);
    if (text.trim() && text !== item.text) {
      onUpdate({ ...item, text: text.trim() });
    } else {
      setText(item.text);
    }
  }

  // ============================================
  // PRIORITY CYCLE
  // ============================================
  function cyclePriority() {
    const order = ["low", "medium", "high"];
    const current = order.indexOf(item.priority || "low");
    const next = order[(current + 1) % order.length];
    onUpdate({ ...item, priority: next });
  }

  const priority = item.priority || "low";
  const priorityColor = PRIORITY_CONFIG[priority]?.color || "var(--priority-low)";

  return (
    <div
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--space-3)",
        padding: "var(--space-3) var(--space-4)",
        background: isDragging
          ? "var(--bg-surface-2)"
          : "var(--bg-surface)",
        borderBottom: "1px solid var(--border)",
        borderLeft: `3px solid ${priorityColor}`,
        opacity: leaving ? 0 : 1,
        transform: leaving ? "translateX(40px)" : "translateX(0)",
        maxHeight: leaving ? 0 : "200px",
        overflow: leaving ? "hidden" : "visible",
        transition: leaving
          ? "opacity 350ms ease, transform 350ms ease, max-height 400ms ease"
          : "background var(--transition-fast)",
        boxShadow: isDragging ? "var(--shadow-md)" : "none",
        cursor: isDragging ? "grabbing" : "default"
      }}
    >
      {/* Drag handle */}
      {!readOnly && (
        <div style={{
          opacity: showActions ? 1 : 0,
          transition: "opacity var(--transition-fast)",
          marginTop: "2px"
        }}>
          <DragHandle
            listeners={dragHandleListeners}
            attributes={dragHandleAttributes}
          />
        </div>
      )}

      {/* Checkbox */}
      <div style={{ marginTop: "2px" }}>
        <Checkbox
          checked={item.checked}
          onChange={handleCheck}
          priority={priority}
        />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Item text */}
        {editingText ? (
          <input
            autoFocus
            value={text}
            onChange={e => setText(e.target.value)}
            onBlur={handleTextSave}
            onKeyDown={e => {
              if (e.key === "Enter") handleTextSave();
              if (e.key === "Escape") { setText(item.text); setEditingText(false); }
            }}
            style={{
              width: "100%",
              fontFamily: "var(--font-body)",
              fontSize: "15px",
              fontWeight: 500,
              color: "var(--text-primary)",
              background: "var(--bg-surface-2)",
              border: "1px solid var(--accent-green)",
              borderRadius: "var(--radius-sm)",
              padding: "2px var(--space-2)",
              outline: "none"
            }}
          />
        ) : (
          <p
            onDoubleClick={() => !readOnly && setEditingText(true)}
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "15px",
              fontWeight: 500,
              color: item.checked ? "var(--text-muted)" : "var(--text-primary)",
              margin: 0,
              textDecoration: item.checked ? "line-through" : "none",
              transition: "color var(--transition-fast)",
              wordBreak: "break-word"
            }}
          >
            {item.text}
          </p>
        )}

        {/* Meta row */}
        <div style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "var(--space-2)",
          marginTop: "var(--space-1)"
        }}>
          <DueDate dueDate={item.dueDate} />
          <SkippedBadge skippedOn={item.skippedOn} />
        </div>

        {/* Notes */}
        {!item.checked && (
          <NotesField
            notes={item.notes}
            onChange={notes => onUpdate({ ...item, notes })}
            readOnly={readOnly}
          />
        )}
      </div>

      {/* Priority + actions */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: "var(--space-2)",
        flexShrink: 0
      }}>

        {/* Priority toggle */}
        {!readOnly && (
          <button
            onClick={cyclePriority}
            title={`Priority: ${PRIORITY_CONFIG[priority].label}`}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "14px",
              padding: "2px",
              opacity: showActions || priority !== "low" ? 1 : 0.3,
              transition: "opacity var(--transition-fast)"
            }}
          >
            {PRIORITY_CONFIG[priority].dot}
          </button>
        )}

        {/* Delete button */}
        {!readOnly && showActions && !item.checked && (
          <button
            onClick={() => onDelete(item.id)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--text-muted)",
              padding: "2px",
              display: "flex",
              alignItems: "center",
              transition: "color var(--transition-fast)"
            }}
            onMouseEnter={e => e.currentTarget.style.color = "var(--status-overdue)"}
            onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 4H12M5 4V2H9V4M3 4L4 12H10L11 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}